import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { onActiveUserChanged, readActiveUserID, scopedStorageKey } from "@/lib/user-scope";

export type Notification = {
  id: string;
  title: string;
  body: string;
  kind: "job" | "wallet" | "bid" | "system" | "connect";
  createdAt: number;
  read: boolean;
};

const STORAGE_KEY = "nebula.notifications.v1";
const STORAGE_WRITE_LIMITS = [50, 20, 5, 0] as const;
const MAX_STORED_TITLE_LENGTH = 160;
const MAX_STORED_BODY_LENGTH = 500;

function compactText(value: string, max: number) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3)).trim()}...`;
}

function compactNotification(item: Notification): Notification {
  return {
    id: String(item.id || ""),
    title: compactText(item.title, MAX_STORED_TITLE_LENGTH),
    body: compactText(item.body, MAX_STORED_BODY_LENGTH),
    kind: item.kind,
    createdAt: Number(item.createdAt || Date.now()),
    read: Boolean(item.read),
  };
}

function readStored(userID: string): Notification[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(STORAGE_KEY, userID));
    return raw ? (JSON.parse(raw) as Notification[]) : null;
  } catch {
    return null;
  }
}
function writeStored(userID: string, items: Notification[]) {
  if (typeof window === "undefined") return;
  const key = scopedStorageKey(STORAGE_KEY, userID);
  for (const limit of STORAGE_WRITE_LIMITS) {
    try {
      const payload = items.slice(0, limit).map(compactNotification);
      window.localStorage.setItem(key, JSON.stringify(payload));
      return;
    } catch {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Ignore storage cleanup failures; notifications must never break the app shell.
      }
    }
  }
}

export function writeStoredNotificationsForTest(userID: string, items: Notification[]) {
  writeStored(userID, items);
}

type Ctx = {
  items: Notification[];
  unread: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clear: () => void;
  push: (n: Omit<Notification, "id" | "createdAt" | "read">) => void;
};

const NotificationsContext = createContext<Ctx | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Notification[]>([]);
  const localID = useRef(0);
  const [activeUserID, setActiveUserID] = useState<string>(() => readActiveUserID());

  useEffect(() => {
    return onActiveUserChanged((nextUserID) => {
      setActiveUserID(nextUserID);
    });
  }, []);

  useEffect(() => {
    const stored = readStored(activeUserID);
    setItems(stored && stored.length ? stored : []);
  }, [activeUserID]);

  const persist = useCallback((next: Notification[]) => {
    setItems(next);
    writeStored(activeUserID, next);
  }, [activeUserID]);

  const push = useCallback<Ctx["push"]>((n) => {
    setItems((prev) => {
      localID.current += 1;
      const nextID = `n-${Date.now()}-${localID.current}`;
      const next = [
        { ...n, id: nextID, createdAt: Date.now(), read: false },
        ...prev,
      ].slice(0, 50);
      writeStored(activeUserID, next);
      return next;
    });
  }, [activeUserID]);

  const value: Ctx = {
    items,
    unread: items.filter((i) => !i.read).length,
    markAllRead: () => persist(items.map((i) => ({ ...i, read: true }))),
    markRead: (id) => persist(items.map((i) => (i.id === id ? { ...i, read: true } : i))),
    clear: () => persist([]),
    push,
  };

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationsProvider>");
  return ctx;
}

export function timeAgo(ts: number): string {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
